import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Chart } from 'chart.js/auto';
import { DeviceService } from 'src/app/services/devices/device.service';
import { TimeFilter, UtilsService } from 'src/app/services/utils/utils.service';
import { FiltersTimeComponent } from 'src/app/components/filters-time/filters-time.component';
import { Colors } from 'src/assets/js/colors';

@Component({
    selector: 'app-analytics',
    imports: [CommonModule, TranslateModule, FiltersTimeComponent],
    templateUrl: './analytics.component.html',
    styleUrl: './analytics.component.scss'
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  serial = '';
  loading = false;
  totalEnergy = 0;
  hasData = false;

  activeFilter: TimeFilter = '7days';
  customStartDate = '';
  customEndDate = '';

  private chart: Chart | null = null;

  constructor(
    private _aRoute: ActivatedRoute,
    private _device: DeviceService,
    private _utils: UtilsService,
  ) { }

  ngOnInit(): void {
    this._aRoute.parent?.params.subscribe((params: Params) => {
      this.serial = params['serial'];
      this.loadGraph();
    });
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  onFilterChange(filter: string): void {
    this.activeFilter = filter as TimeFilter;
    if (filter !== 'custom') {
      this.loadGraph();
    }
  }

  onCustomDateChange(dates: { startDate: string; endDate: string }): void {
    this.customStartDate = dates.startDate;
    this.customEndDate = dates.endDate;
    this.loadGraph();
  }

  private loadGraph(): void {
    if (!this.serial) {
      return;
    }
    this.loading = true;
    const { from, to } = this._utils.resolveDateRange(this.activeFilter, this.customStartDate, this.customEndDate);
    const dateMax = this._utils.getFormatedLocalDate(to, 'YYYY-MM-DD');
    const dateMin = this._utils.getFormatedLocalDate(from, 'YYYY-MM-DD');

    this._device.getHistoricGraphView(this.serial, dateMax, dateMin).subscribe({
      next: (resp) => {
        this.renderChart(Array.isArray(resp?.data) ? resp.data : []);
        this.loading = false;
      },
      error: () => {
        this.renderChart([]);
        this.loading = false;
      }
    });
  }

  private renderChart(rows: any[]): void {
    this.hasData = rows.length > 0;
    const labels = rows.map(row => this._utils.getFormatedLocalDate(row.hour, 'DD/MM HH:mm'));
    // ponytail: 'homeEnergy' es el campo de los cargadores, sin verificar con una respuesta real de
    // AstroLum; 'energy' por si el backend lo nombra distinto.
    const values = rows.map(row => Number(row.homeEnergy ?? row.energy ?? 0) || 0);
    this.totalEnergy = values.reduce((acc, value) => acc + value, 0);

    if (this.chart) {
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = values;
      this.chart.update();
      return;
    }

    const canvas = document.getElementById('chart-analytics') as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }
    this.chart = new Chart(canvas.getContext('2d')!, {
      type: 'bar',
      data: {
        labels,
        datasets: [{ label: 'Wh', data: values, backgroundColor: Colors.primary }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { y: { beginAtZero: true } }
      }
    });
  }
}
